/**
 * graphMath — pure, dependency-free helpers for the Graph block's chart geometry.
 * Extracted so the load-bearing numeric logic (value coercion, zero-baseline tick
 * generation, bar heights, pie percentages) is unit-testable without rendering.
 */

/** Coerce any datapoint value to a finite number (blank / bad input -> 0). */
export function num(v) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Evenly spaced y ticks from a zero baseline up to (at least) maxVal.
 * Always includes 0 as the first tick so every cartesian chart has a zero axis.
 */
export function yTicks(maxVal, count = 4) {
  const top = maxVal <= 0 ? 1 : maxVal;
  const step = top / count;
  return Array.from({ length: count + 1 }, (_, i) => +(step * i).toFixed(2));
}

/** The domain top used for scaling (the last, largest tick). */
export function domainTop(maxVal, count = 4) {
  const t = yTicks(maxVal, count);
  return t[t.length - 1];
}

/**
 * Bar heights as a fraction (0..1) of the plot height, scaled against the tick
 * domain top. A zero value -> 0 height; the max value -> full domain fraction.
 */
export function barFractions(values, count = 4) {
  const nums = values.map(num);
  const max = nums.reduce((m, v) => (v > m ? v : m), 0);
  const top = domainTop(max, count);
  return nums.map((v) => (top === 0 ? 0 : Math.max(v, 0) / top));
}

/** Pie/donut slice percentages (integers, share of the positive total). */
export function slicePercents(values) {
  const nums = values.map((v) => Math.max(num(v), 0));
  const total = nums.reduce((s, v) => s + v, 0);
  return nums.map((v) => (total === 0 ? 0 : Math.round((v / total) * 100)));
}

/**
 * Ellipsize a data-point label for an SVG <text> node, which has NO native
 * wrap/truncate: cap the string to `maxChars` glyphs, appending a single-glyph
 * ellipsis (…) when clipped so a long label can never overflow / collide inside
 * the fixed chart viewBox. Non-string / nullish -> "". `maxChars` is floored to a
 * minimum of 1 (a 1-char budget renders just the ellipsis). Guards EW-1063.
 */
export function truncateLabel(label, maxChars = 12) {
  const s = label == null ? "" : String(label);
  const max = Math.max(1, Math.floor(maxChars));
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}
